/**
 * Shared Access Service
 * 
 * Handles all shared access (secondary users) related API calls.
 */

import api from './api';
import type {
  SharedAccess,
  CreateSharedAccessRequest,
  UpdateSharedAccessRequest,
  ApiResponse,
} from '@budgira/shared';

export interface SharedAccessWithInvitation extends SharedAccess {
  invitationSent?: boolean;
}

export interface SendInvitationResponse {
  sent: boolean;
  message: string;
}

export interface GuestProfile {
  firstName: string;
  lastName: string;
  email: string;
}

export const sharedAccessService = {
  /**
   * Get all shared accesses for the current user
   */
  async list(): Promise<ApiResponse<SharedAccess[]>> {
    const response = await api.get<ApiResponse<SharedAccess[]>>('/shared-accesses');
    return response.data;
  },

  /**
   * Get a specific shared access
   */
  async getById(id: string): Promise<ApiResponse<SharedAccess>> {
    const response = await api.get<ApiResponse<SharedAccess>>(`/shared-accesses/${id}`);
    return response.data;
  },

  /**
   * Create a new shared access
   * @param data - Creation data
   * @param sendInvitation - Whether to send invitation email (default: true)
   */
  async create(
    data: CreateSharedAccessRequest,
    sendInvitation: boolean = true
  ): Promise<ApiResponse<SharedAccessWithInvitation>> {
    const response = await api.post<ApiResponse<SharedAccessWithInvitation>>(
      `/shared-accesses?sendInvitation=${sendInvitation}`,
      data
    );
    return response.data;
  },

  /**
   * Update a shared access
   */
  async update(id: string, data: UpdateSharedAccessRequest): Promise<ApiResponse<SharedAccess>> {
    const response = await api.put<ApiResponse<SharedAccess>>(`/shared-accesses/${id}`, data);
    return response.data;
  },

  /**
   * Block a shared access
   */
  async block(id: string): Promise<ApiResponse<SharedAccess>> {
    const response = await api.post<ApiResponse<SharedAccess>>(`/shared-accesses/${id}/block`);
    return response.data;
  },

  /**
   * Unblock a shared access
   */
  async unblock(id: string): Promise<ApiResponse<SharedAccess>> {
    const response = await api.post<ApiResponse<SharedAccess>>(`/shared-accesses/${id}/unblock`);
    return response.data;
  },

  /**
   * Delete a shared access permanently
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`/shared-accesses/${id}`);
    return response.data;
  },

  /**
   * Send (or resend) invitation email
   */
  async sendInvitation(id: string): Promise<ApiResponse<SendInvitationResponse>> {
    const response = await api.post<ApiResponse<SendInvitationResponse>>(
      `/shared-accesses/${id}/send-invitation`
    );
    return response.data;
  },

  // ============================================
  // GUEST PROFILE ROUTES (for linked users)
  // ============================================

  /**
   * Get own profile from shared access (for guest users)
   */
  async getMyProfile(): Promise<ApiResponse<GuestProfile | null>> {
    const response = await api.get<ApiResponse<GuestProfile | null>>('/shared-accesses/my-profile');
    return response.data;
  },

  /**
   * Update own profile in shared access (for guest users)
   */
  async updateMyProfile(firstName: string, lastName: string): Promise<ApiResponse<{ firstName: string; lastName: string }>> {
    const response = await api.put<ApiResponse<{ firstName: string; lastName: string }>>('/shared-accesses/my-profile', {
      firstName,
      lastName,
    });
    return response.data;
  },
};

export default sharedAccessService;
